// Import global utils
import { getUniqueID } from '@automator/shared/utils/get-unique-id';
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import html-entities
import { encode } from 'html-entities';

// Import definitions
import { getIntegration } from '@automator/builder/definitions/integrations';

import {
	parseDynamicSentence
} from '@automator/builder/_legacy/js/utilities';

import {
	getConditionPostData
} from '@automator/builder/_legacy/js/item/conditions/utilities';

/**
 * Creates a condition
 */
export default class Condition {
	// Properties
	integrationCode = '';

	conditionCode = '';

	/**
	 * The value of the fields
	 *
	 * @type {Object}
	 */
	fields = {};

	/**
	 * This is ONLY used if the condition is missing
	 *
	 * @type {Object}
	 */
	backup = {};

	/**
	 * The ConditionsGroup of this condition
	 *
	 * @type {ConditionGroup}
	 */
	conditionsGroup;

	/**
	 * Creates an unique ID
	 *
	 * @param {Object} options Object with options
	 * @param {String} id String with unique ID to target this conditions block
	 * 
	 * @return {undefined}
	 */
	constructor( { id } ) {
		// Get or create unique ID
		this.id = ! isEmpty( id ) ? id : getUniqueID();
	}

	/**
	 * Checks whether a condition is missing
	 *
	 * @return {Boolean} True if it's missing
	 */
	get isMissing() {
		// Check if the condition is defined in the PHP
		return ! isDefined( this.conditionPostData );
	}

	/**
	 * Returns an object with sanitazed data for the backend
	 *
	 * @return {Object} Sanitazed data
	 */
	get formattedForMeta() {
		// Default response
		let conditionForMeta = {};

		/**
		 * The backend expects
		 *
		 * {
		 * 	 integration: 'WP', // String with the integration code
		 * 	 condition: 'TOKEN_MEETS_CONDITION', // String with the condition code
		 * 	 fields: { // Object with the field values
		 * 	   FIELD_ID: 'Field value',
		 * 	   FIELD_ID_2: 'Field value'
		 * 	 },
		 * 	 backup: { // Object with data used when the condition is missing
		 * 	   nameDynamic: '', // The name of the sentence
		 * 	   integrationName: '', // The name of the integration
		 * 	 }
		 * }
		 */
		conditionForMeta = {
			id: this.id,

			integration: this.integrationCode,
			condition: this.conditionCode,
			fields: this.fields,

			// We will save this just in case the condition gets removed in the future
			// If the condition is missing, DON'T UPDATE the value, otherwise it will get removed
			backup: this.isMissing ? this.backup : {
				// Get the dynamic name of the condition
				nameDynamic: this.conditionPostData.dynamic_name,

				// HTML title for the logs
				titleHTML: encode( this.$dynamicSentence.outerHTML ),

				// Integration name
				integrationName: this.integration.name,
			}
		};

		return conditionForMeta;
	}

	/**
	 * Returns the dynamic sentence of the condition with the current values of the fields
	 *
	 * @return {Node} A node with the sentence
	 */
	get $dynamicSentence() {
		// Create node
		const $sentence = parseDynamicSentence( {
			dynamicSentence: ! this.isMissing ? this.conditionPostData.dynamic_name : this.backup.nameDynamic,
			fields: this.fields,

			// If it's available, pass the ID of the action with the lowest priority as the item ID, so we can know what tokens are available. Otherwise, pass the ID of the filter itself
			itemID: this.conditionsGroup.actionWithLowestPriority || this.conditionsGroup.id
		} );

		return $sentence;
	}

	/**
	 * Returns the condition base data
	 *
	 * @return {Object} The condition base data
	 */
	get conditionPostData() {
		return getConditionPostData( {
			integrationCode: this.integrationCode,
			conditionCode: this.conditionCode
		} );
	}

	/**
	 * Returns the integration of the condition
	 *
	 * @return {Integration} The integration
	 */
	get integration() {
		return getIntegration( this.integrationCode );
	}
}
